import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import HintBar from '@/components/HintBar';

const HINTS = [
  'Particles travel from your role nucleus to their destination pools',
  'Each destination card = a new workforce pool from this role',
  'Trail density = hours routed to that pool',
  'Generate Blueprint → to produce the full transformation plan',
];

/* ================================================================
   Recompose — Recipe 3.18 · Convergence rails
   Vertical: canvas (fork + rails) · col labels · 5 destination cards
   ================================================================ */

const BUCKET_ORDER = ['bot', 'borrow', 'build', 'bi', 'buy'];
const BUCKET_LABEL = { bot: 'Bot', borrow: 'Borrow', build: 'Build', bi: 'BI', buy: 'Buy' };
const BUCKET_TITLE = {
  bot:    'AI Deployment',
  borrow: 'Fractional Pool',
  build:  'Internal Build',
  bi:     'Inclusion Pathway',
  buy:    'External Hire',
};
const BUCKET_SUB = {
  bot:    'Automated workloads',
  borrow: 'Interim & advisory capacity',
  build:  'Strategic human work',
  bi:     'Accessible role redesign',
  buy:    'New permanent hires',
};

const STEPS = [
  { token: '✓', label: 'Ingest',      path: '/analysis',    done: true },
  { token: '✓', label: 'Deconstruct', path: '/deconstruct', done: true },
  { token: '✓', label: 'Analyze',     path: '/analyze',     done: true },
  { token: '✓', label: 'Sort · 4B',   path: '/sort',        done: true },
  { token: '✓', label: 'Recompose',   path: '/recompose',   done: true, active: true },
];

function readBucketColors() {
  const cs = getComputedStyle(document.documentElement);
  const pick = (name, fallback) => (cs.getPropertyValue(name).trim() || fallback);
  return {
    bot:    pick('--bot',    '#00D4C8'),
    borrow: pick('--borrow', '#06B6D4'),
    build:  pick('--build',  '#C9A84C'),
    bi:     pick('--bi',     '#E8B96A'),
    buy:    pick('--buy',    '#EC4899'),
    teal:   pick('--teal',   '#00D4C8'),
    text:   pick('--text',   '#FFFFFF'),
    t3:     pick('--t3',     'rgba(255,255,255,0.72)'),
    t4:     pick('--t4',     'rgba(255,255,255,0.48)'),
    line:   pick('--line',   'rgba(255,255,255,0.10)'),
  };
}

function loadRole() {
  try {
    const stored = localStorage.getItem('reai_role');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.activities?.length) return parsed;
    }
  } catch { /* ignore */ }

  try {
    const raw = localStorage.getItem('analysis_mol_results');
    if (raw) {
      const mol = JSON.parse(raw);
      const r = (mol.roles || [])[0];
      if (r) {
        const tasks = r.task_breakdown || [];
        return {
          name: r.original_title || r.role_title || 'Role',
          function: localStorage.getItem('analysis_function') || '',
          hours: 40,
          activities: [
            {
              name: 'All tasks',
              hours: tasks.reduce((s, t) => s + (t.estimated_hours || 0), 0),
              subs: tasks.map((t) => ({
                name: t.task,
                hours: t.estimated_hours || 0,
                deployment: t.category_4b || 'build',
              })),
            },
          ],
        };
      }
    }
  } catch { /* ignore */ }

  return null;
}

function loadOverrides() {
  try { return JSON.parse(localStorage.getItem('reai_deploy') || '{}'); }
  catch { return {}; }
}

const subIdOf = (ai, si) => `${ai}-${si}`;

const EmptyState = ({ onGo }) => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, color: 'var(--text)', fontFamily: 'var(--fs)', position: 'relative', zIndex: 2 }}>
    <div className="eyebrow eye-teal">No role loaded</div>
    <p style={{ color: 'var(--t3)', fontSize: 14, maxWidth: 420, textAlign: 'center', lineHeight: 1.6 }}>
      Run Analysis first so the role can be recomposed into 4B pools.
    </p>
    <button className="btn-teal" onClick={onGo}>Go to Analysis →</button>
  </div>
);

// Cubic bezier helper
const bez = (t, p0, p1, p2, p3) => {
  const mt = 1 - t;
  return mt * mt * mt * p0 + 3 * mt * mt * t * p1 + 3 * mt * t * t * p2 + t * t * t * p3;
};

const Recompose = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const rafRef = useRef(null);

  // Global particles → ambient
  useEffect(() => {
    const api = window.__particles;
    if (!api) return;
    api.setMode('ambient');
    return () => api.setMode('ambient');
  }, []);

  const role = useMemo(() => loadRole(), []);
  const overrides = useMemo(() => loadOverrides(), []);

  const flatSubs = useMemo(() => {
    if (!role) return [];
    return (role.activities || []).flatMap((a, ai) =>
      (a.subs || []).map((s, si) => ({
        id: subIdOf(ai, si),
        name: s.name,
        hours: s.hours || 0,
        origDeployment: s.deployment || 'build',
        activityIdx: ai,
        subIdx: si,
        activityName: a.name,
      }))
    );
  }, [role]);

  const subsByBucket = useMemo(() => {
    const g = { bot: [], borrow: [], build: [], bi: [], buy: [] };
    flatSubs.forEach((s) => {
      const eff = overrides[s.id] || s.origDeployment || 'build';
      if (g[eff]) g[eff].push(s);
      else g.build.push(s);
    });
    return g;
  }, [flatSubs, overrides]);

  const bucketHours = useMemo(() => {
    const out = {};
    BUCKET_ORDER.forEach((b) => {
      out[b] = (subsByBucket[b] || []).reduce((s, x) => s + (x.hours || 0), 0);
    });
    return out;
  }, [subsByBucket]);

  const totalHours = useMemo(
    () => Object.values(bucketHours).reduce((s, h) => s + h, 0),
    [bucketHours]
  );

  const populatedBuckets = BUCKET_ORDER.filter((b) => (subsByBucket[b] || []).length > 0);

  // ── Canvas rendering ─────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    const stage = stageRef.current;
    if (!canvas || !stage) return;

    const ctx = canvas.getContext('2d');
    const colors = readBucketColors();
    let W = 0, H = 0, dpr = 1;

    const measure = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = stage.getBoundingClientRect();
      W = Math.max(480, rect.width);
      H = Math.max(260, rect.height);
      canvas.width = Math.floor(W * dpr);
      canvas.height = Math.floor(H * dpr);
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    // Layout anchors
    const layout = () => {
      const nucleus = { x: W * 0.16, y: H * 0.5, r: 26 };
      const fork = { x: W * 0.44, y: H * 0.5, r: 7 };
      const destX = W * 0.82;
      // 72px reserves halo radius (r*1.8 ≈ 43px) PLUS visual breathing
      // room so BUY's halo doesn't crowd the "Recomposed" tier label
      // immediately below the canvas stage. At H ≥ 512 (laptop/projector)
      // the 14% branch dominates, so this only kicks in on shorter
      // viewports like iPad landscape (H≈380) where the extra 28px buffer
      // above 44 keeps clusters clear of the sibling label row.
      // Keep in sync with halo coefficient at L321 (ctx.arc radius * 1.8).
      const HALO_PAD = Math.max(0.14 * H, 72);
      const destTop = HALO_PAD;
      const destBot = H - HALO_PAD;
      const dests = BUCKET_ORDER.map((bucket, i) => {
        const frac = BUCKET_ORDER.length === 1 ? 0.5 : i / (BUCKET_ORDER.length - 1);
        const y = destTop + frac * (destBot - destTop);
        const hrs = bucketHours[bucket] || 0;
        const r = Math.min(24, Math.max(8, Math.sqrt(Math.max(1, hrs)) * 1.8));
        const populated = (subsByBucket[bucket] || []).length > 0;
        return { bucket, x: destX, y, r, hrs, populated, color: colors[bucket] };
      });
      return { nucleus, fork, dests };
    };

    // Particle streams — one ring of phase values per destination
    // Count scales with hours so busier rails feel denser
    const streams = BUCKET_ORDER.map((bucket) => {
      const hrs = bucketHours[bucket] || 0;
      const n = Math.min(8, Math.max(2, Math.round(Math.sqrt(Math.max(1, hrs)))));
      const phases = Array.from({ length: n }, (_, i) => i / n);
      return { bucket, phases };
    });

    const speedFor = (hrs) => 0.00035 + Math.min(0.0012, hrs * 0.00002); // px-independent

    let last = performance.now();
    const draw = (now) => {
      const dt = Math.min(33, now - last);
      last = now;
      const { nucleus, fork, dests } = layout();

      ctx.clearRect(0, 0, W, H);

      // ── Rails (nucleus → fork → each dest)
      dests.forEach((d) => {
        if (!d.populated) return;
        const color = d.color;

        // Nucleus → fork (shared trunk, drawn once with teal later)

        // Fork → dest bezier: control points shape the fan
        const cp1 = { x: fork.x + (d.x - fork.x) * 0.4, y: fork.y };
        const cp2 = { x: d.x - (d.x - fork.x) * 0.4, y: d.y };

        ctx.strokeStyle = color;
        ctx.globalAlpha = 0.45;
        ctx.lineWidth = 1.25;
        ctx.beginPath();
        ctx.moveTo(fork.x, fork.y);
        ctx.bezierCurveTo(cp1.x, cp1.y, cp2.x, cp2.y, d.x, d.y);
        ctx.stroke();
        ctx.globalAlpha = 1;
      });

      // Nucleus → fork trunk (single teal line)
      ctx.strokeStyle = colors.teal;
      ctx.globalAlpha = 0.55;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(nucleus.x, nucleus.y);
      ctx.lineTo(fork.x, fork.y);
      ctx.stroke();
      ctx.globalAlpha = 1;

      // ── Nucleus (role)
      // Outer glow
      const grad = ctx.createRadialGradient(nucleus.x, nucleus.y, 0, nucleus.x, nucleus.y, nucleus.r * 2.6);
      grad.addColorStop(0, colors.teal + '55');
      grad.addColorStop(1, colors.teal + '00');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(nucleus.x, nucleus.y, nucleus.r * 2.6, 0, Math.PI * 2);
      ctx.fill();
      // Core
      ctx.fillStyle = colors.teal + 'CC';
      ctx.beginPath();
      ctx.arc(nucleus.x, nucleus.y, nucleus.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = colors.teal;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(nucleus.x, nucleus.y, nucleus.r, 0, Math.PI * 2);
      ctx.stroke();

      // Nucleus label
      ctx.fillStyle = colors.text;
      ctx.font = "700 11px 'Manrope', sans-serif";
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const nameMax = nucleus.r * 3.2;
      const roleShort = role.name.length > 18 ? role.name.slice(0, 18) + '…' : role.name;
      ctx.fillText(roleShort, nucleus.x, nucleus.y + nucleus.r + 18);
      ctx.font = "9px 'Overpass Mono', monospace";
      ctx.fillStyle = colors.t4;
      ctx.fillText(`${totalHours}h`, nucleus.x, nucleus.y + nucleus.r + 34);
      void nameMax;

      // ── Fork point
      ctx.fillStyle = colors.t3;
      ctx.beginPath();
      ctx.arc(fork.x, fork.y, fork.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = colors.line;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(fork.x, fork.y, fork.r + 4, 0, Math.PI * 2);
      ctx.stroke();

      // ── Destinations
      dests.forEach((d) => {
        const color = d.color;
        const alpha = d.populated ? 1 : 0.35;

        // Halo
        ctx.globalAlpha = alpha * 0.5;
        ctx.fillStyle = color + '55';
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r * 1.8, 0, Math.PI * 2);
        ctx.fill();

        // Core
        ctx.globalAlpha = alpha;
        ctx.fillStyle = color + 'CC';
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1;

        // Label
        ctx.fillStyle = d.populated ? color : colors.t4;
        ctx.font = "600 10px 'Overpass Mono', monospace";
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(BUCKET_LABEL[d.bucket].toUpperCase(), d.x + d.r + 10, d.y - 6);
        ctx.fillStyle = colors.t4;
        ctx.font = "9px 'Overpass Mono', monospace";
        ctx.fillText(`${d.hrs}h`, d.x + d.r + 10, d.y + 8);
      });

      // ── Travelling particles (nucleus → fork → dest)
      streams.forEach((stream) => {
        const d = dests.find((x) => x.bucket === stream.bucket);
        if (!d || !d.populated) return;
        const cp1 = { x: fork.x + (d.x - fork.x) * 0.4, y: fork.y };
        const cp2 = { x: d.x - (d.x - fork.x) * 0.4, y: d.y };
        const speed = speedFor(d.hrs);

        stream.phases = stream.phases.map((p) => {
          let np = p + speed * dt;
          while (np > 1) np -= 1;
          return np;
        });

        stream.phases.forEach((p) => {
          let x, y;
          if (p < 0.5) {
            // Nucleus → fork (linear)
            const t = p / 0.5;
            x = nucleus.x + (fork.x - nucleus.x) * t;
            y = nucleus.y + (fork.y - nucleus.y) * t;
          } else {
            // Fork → dest (cubic bezier)
            const t = (p - 0.5) / 0.5;
            x = bez(t, fork.x, cp1.x, cp2.x, d.x);
            y = bez(t, fork.y, cp1.y, cp2.y, d.y);
          }
          ctx.fillStyle = d.color + '88';
          ctx.beginPath();
          ctx.arc(x, y, 2.5, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = d.color;
          ctx.beginPath();
          ctx.arc(x, y, 1.5, 0, Math.PI * 2);
          ctx.fill();
        });
      });

      rafRef.current = requestAnimationFrame(draw);
    };

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(() => measure());
    });
    ro.observe(stage);
    measure();
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [role, bucketHours, subsByBucket, totalHours]);

  const handleReset = () => {
    localStorage.removeItem('reai_role');
    localStorage.removeItem('reai_deploy');
    navigate('/analysis');
  };

  if (!role) return <EmptyState onGo={() => navigate('/analysis')} />;

  const totalSubs = flatSubs.length;

  return (
    <div style={{
      height: '100vh',
      overflow: 'hidden',
      color: 'var(--text)',
      fontFamily: 'var(--fs)',
      position: 'relative',
      zIndex: 2,
      paddingTop: 'var(--nav-h)',
    }}>
      {/* ── Top nav ────────────────────────────────────── */}
      <header className="nav">
        <span className="nav-brand">re<span className="ai">AI</span>magination</span>
        <span className="nav-sep" />
        <span className="nav-lbl">Product flow</span>
        <nav className="nav-pages" style={{ marginLeft: 'auto' }}>
          <button onClick={() => navigate('/signal-room')}     className="np">Signal Room</button>
          <button onClick={() => navigate('/intelligence')}    className="np">Intelligence</button>
          <button onClick={() => navigate('/blueprint/cockpit')} className="np">Blueprint</button>
          <button onClick={() => navigate('/stage')}           className="np">Stage</button>
          <button onClick={() => navigate('/showcase/ai-tools')} className="np">Showcase</button>
        </nav>
      </header>

      {/* ── Chrome bar · 5-step stepper (all done) ───── */}
      <div className="chro-bar">
        <span className="cb-brand">re<span className="ai">AI</span>magination</span>
        <div className="cb-tabs">
          {STEPS.map((s) => (
            <button
              key={s.path}
              className={`ct${s.active ? ' on' : ''}`}
              onClick={() => !s.active && navigate(s.path)}
              style={{ opacity: 1 }}
            >
              <span style={{ marginRight: 6, color: 'var(--teal)' }}>{s.token}</span>
              {s.label}
            </button>
          ))}
        </div>
        <div className="cb-right">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--t2)' }}>
            <span className="act-dot" style={{ background: 'var(--teal)', boxShadow: '0 0 5px var(--teal)' }} />
            {role.name}
          </span>
          <button
            onClick={handleReset}
            style={{
              padding: '4px 10px', fontSize: 10, fontFamily: 'var(--fm)',
              color: 'var(--t3)', background: 'transparent',
              border: '1px solid var(--line)', borderRadius: 'var(--r-sm)',
              cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.12em',
            }}
          >
            Reset
          </button>
        </div>
      </div>

      <HintBar screenId="recompose" hints={HINTS} />

      {/* ── Recompose stage ─────────────────────────── */}
      <div
        className="recompose-main"
        style={{
          height: 'calc(100vh - var(--nav-h) - var(--chro-h) - var(--hint-h, 0px) - var(--status-h))',
          overflow: 'hidden',
        }}
      >
        <div
          className="convergence-stage"
          ref={stageRef}
          style={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            background: 'rgba(10,10,26,0.08)',
          }}
        >
          <canvas
            id="convergence-cv"
            ref={canvasRef}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              display: 'block',
            }}
          />
        </div>

        <div className="col-labels">
          <span>Original</span>
          <span>Fork</span>
          <span>Recomposed</span>
        </div>

        <div className="dest-grid">
          {BUCKET_ORDER.map((bucket) => {
            const subs = subsByBucket[bucket] || [];
            const hrs = bucketHours[bucket] || 0;
            const empty = subs.length === 0;
            return (
              <div
                key={bucket}
                className="dest-card"
                style={{
                  borderColor: empty ? 'var(--line-s)' : `var(--${bucket})`,
                  opacity: empty ? 0.5 : 1,
                }}
              >
                <div className="dest-kind" style={{ color: empty ? 'var(--t4)' : `var(--${bucket})` }}>
                  Pool · {BUCKET_LABEL[bucket]}
                </div>
                <div className="dest-title">{BUCKET_TITLE[bucket]}</div>
                <div className="dest-sub">{BUCKET_SUB[bucket]}</div>
                <div className="dest-hours" style={{ color: empty ? 'var(--t4)' : `var(--${bucket})` }}>
                  {empty ? '—' : `${hrs}h`}
                </div>
                <div className="dest-items">
                  {empty
                    ? <span style={{ color: 'var(--t4)' }}>No subs routed</span>
                    : subs.slice(0, 4).map((s, i) => (
                        <div key={i} style={{ fontSize: 11, color: 'var(--t3)', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {s.name.length > 26 ? s.name.slice(0, 26) + '…' : s.name}
                          <span style={{ color: 'var(--t4)', marginLeft: 6 }}>{s.hours}h</span>
                        </div>
                      ))}
                  {!empty && subs.length > 4 && (
                    <div style={{ fontSize: 10, color: 'var(--t4)', marginTop: 4, fontStyle: 'italic' }}>
                      +{subs.length - 4} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Generate Blueprint CTA */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, paddingTop: 4 }}>
          <div>
            <div className="eyebrow eye-teal">Total reclaimed</div>
            <div className="gap-num" style={{ fontSize: 28, color: 'var(--teal)', marginTop: 2 }}>
              {totalHours}h · {populatedBuckets.length} pool{populatedBuckets.length === 1 ? '' : 's'}
            </div>
          </div>
          <button className="btn-teal" onClick={() => navigate('/blueprint/cockpit')}>
            Generate Blueprint →
          </button>
        </div>
      </div>

      {/* ── Status bar ───────────────────────────────── */}
      <div className="status-bar">
        <span className="st-live">● Live</span>
        <span className="st-sep">·</span>
        <span>session</span>
        <span className="st-sep">·</span>
        <span>recompose</span>
        <span className="st-sep">·</span>
        <span>convergence</span>
        <span className="st-sep">·</span>
        <span>particles · {totalSubs}</span>
      </div>
    </div>
  );
};

export default Recompose;
